import { Check } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { formatPrice } from './statusCopy'
import type { BillingPlan } from '../types'

const formatStorage = (bytes: number | null): string =>
  bytes === null ? 'Unmetered' : `${Math.round(bytes / 1024 ** 3)} GB per person`

const seatsLabel = (seats: number): string =>
  `${Math.max(1, seats)} ${Math.max(1, seats) === 1 ? 'seat' : 'seats'}`

const formatCompanies = (max: number | null): string =>
  max === null ? 'Unlimited client companies' : `Up to ${max} client companies`

export function PlanCard({
  plan,
  trialDaysLeft,
  seats,
  current,
  busy,
  disabled,
  customDomainsAvailable,
  onChoose,
}: {
  plan: BillingPlan
  /**
   * Trial days a Checkout started now would keep: the workspace's own trial,
   * never a fresh one. Null when none would be included.
   */
  trialDaysLeft: number | null
  /** Billable seats today (active users, admins included). */
  seats: number
  /** The plan this workspace is on now — priced differently from the others. */
  current: boolean
  busy: boolean
  disabled: boolean
  /** False until the custom-domain infrastructure is live: the feature isn't advertised before then. */
  customDomainsAvailable: boolean
  onChoose: (plan: string) => void
}): React.ReactElement {
  return (
    <div
      className={`flex flex-col rounded-xl border p-5 ${
        current
          ? 'border-primary bg-primary/5 dark:bg-primary/10'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{plan.name}</h3>
        {current && <Badge variant="success">Current plan</Badge>}
      </div>

      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{plan.description}</p>

      <p className="mt-4">
        <span className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
          {formatPrice(plan.monthlyPriceCents)}
        </span>
        {plan.monthlyPriceCents !== null && (
          <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
            per active user / month
          </span>
        )}
      </p>
      {plan.monthlyPriceCents !== null && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {seatsLabel(seats)} today ≈ {formatPrice(plan.monthlyPriceCents * Math.max(1, seats))}
          /month. Invited users are free until they activate.
        </p>
      )}
      {plan.purchasable && trialDaysLeft !== null && trialDaysLeft > 0 && (
        <p className="mt-1 text-xs font-medium text-primary">
          No charge for the {trialDaysLeft === 1 ? 'last day' : `${trialDaysLeft} days`} left in
          your trial
        </p>
      )}

      <ul className="mt-4 flex-1 space-y-2 text-sm text-gray-700 dark:text-gray-300">
        <Feature>{formatCompanies(plan.maxCompanies)}</Feature>
        <Feature>{formatStorage(plan.storagePerUserBytes)}</Feature>
        {plan.features.includes('desktop.time_tracking') && (
          <Feature>Desktop time tracking and screenshots</Feature>
        )}
        {customDomainsAvailable && plan.features.includes('tenant.custom_domain') && (
          <Feature>Your own domain (e.g. hr.yourcompany.com)</Feature>
        )}
      </ul>

      <div className="mt-5">
        {plan.purchasable ? (
          <Button
            className="w-full"
            variant={current ? 'secondary' : 'primary'}
            disabled={disabled || busy || current}
            onClick={() => onChoose(plan.key)}
          >
            {current ? 'Your current plan' : busy ? 'Opening Stripe…' : `Choose ${plan.name}`}
          </Button>
        ) : (
          // No Stripe price, so there is nothing to buy. Saying "contact us"
          // beats a disabled button that never explains itself.
          <Button
            className="w-full"
            variant="secondary"
            onClick={() => {
              window.location.href = 'mailto:sales@kaamco.com?subject=Enterprise%20plan'
            }}
          >
            Contact sales
          </Button>
        )}
      </div>
    </div>
  )
}

function Feature({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <li className="flex items-start gap-2">
      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
      <span>{children}</span>
    </li>
  )
}
